import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { PhoneIcon, MailIcon, MapPinIcon, CheckCircleIcon, AlertCircleIcon, LoaderIcon } from 'lucide-react';
// ─────────────────────────────────────────────────────────────────────────────
// FORM DELIVERY — Web3Forms (free, no backend required)
//
// HOW TO ACTIVATE:
//   1. Go to https://web3forms.com/ and enter Cinnabunnsib@gmail.com
//   2. Web3Forms will email you an Access Key
//   3. Paste it below in place of "REPLACE_WITH_YOUR_WEB3FORMS_ACCESS_KEY"
//   4. Every submission will be delivered to Cinnabunnsib@gmail.com
//
// The free tier allows 250 submissions per month. No signup, no credit card.
// ─────────────────────────────────────────────────────────────────────────────
const WEB3FORMS_ACCESS_KEY = '06983205-32ed-43af-a56a-c423e49925cb';
const RECIPIENT_EMAIL = 'Cinnabunnsib@gmail.com';
type Status = 'idle' | 'submitting' | 'success' | 'error';
export function Contact() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    interests: '',
    message: ''
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');
    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `New Consultation Request from ${formData.name}`,
        from_name: 'Madam Yas Website',
        // Web3Forms delivers to the email tied to the access key.
        // (Add a fallback reply-to so replies go to the inquirer.)
        replyto: formData.email,
        // Body fields
        Name: formData.name,
        Email: formData.email,
        Phone: formData.phone,
        'Experience Level': formData.experience,
        'Areas of Interest': formData.interests,
        Message: formData.message
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          experience: '',
          interests: '',
          message: ''
        });
      } else {
        setStatus('error');
        setErrorMessage(data.message || 'Submission failed. Please try again or call directly.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('Network error. Please try again or call directly.');
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <section id="contact" ref={ref} className="py-24 lg:py-32 bg-ink" data-id="element-77">
      <div className="max-w-6xl mx-auto px-6 lg:px-12" data-id="element-78">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-79">
          <p className="text-gold text-xs uppercase tracking-[0.3em] mb-4 font-cinzel" data-id="element-80">
            Contact & Booking
          </p>
          <h2 className="font-cormorant text-bone text-4xl lg:text-6xl font-light mb-6" data-id="element-81">
            Begin Your Journey
          </h2>
          <p className="text-silver-muted text-lg max-w-2xl mx-auto" data-id="element-82">
            Ready to explore? Complete the form below or reach out directly to
            schedule your consultation.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12" data-id="element-83">
          {/* Contact Info */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="space-y-8" data-id="element-84">
            <div data-id="element-85">
              <h3 className="font-cormorant text-bone text-2xl mb-6" data-id="element-86">
                Get in Touch
              </h3>
              <div className="space-y-4" data-id="element-87">
                <div className="flex items-start gap-4" data-id="element-88">
                  <PhoneIcon size={20} className="text-gold mt-1" data-id="element-89" />
                  <div data-id="element-90">
                    <p className="text-silver-muted text-sm uppercase tracking-wider mb-1" data-id="element-91">
                      Phone
                    </p>
                    <a href="tel:5625480191" className="text-bone hover:text-gold transition-colors" data-id="element-92">
                      562.548.0191
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-93">
                  <MailIcon size={20} className="text-gold mt-1" data-id="element-94" />
                  <div data-id="element-95">
                    <p className="text-silver-muted text-sm uppercase tracking-wider mb-1" data-id="element-96">
                      Email
                    </p>
                    <a href={`mailto:${RECIPIENT_EMAIL}`} className="text-bone hover:text-gold transition-colors break-all" data-id="element-97">
                      {RECIPIENT_EMAIL}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-98">
                  <MapPinIcon size={20} className="text-gold mt-1" data-id="element-99" />
                  <div data-id="element-100">
                    <p className="text-silver-muted text-sm uppercase tracking-wider mb-1" data-id="element-101">
                      Location
                    </p>
                    <p className="text-bone" data-id="element-102">
                      Private Studio
                      <br data-id="element-103" />
                      Los Angeles Area
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-onyx border border-gold/20 p-6" data-id="element-104">
              <h4 className="font-cormorant text-bone text-xl mb-4" data-id="element-105">
                Session Rates
              </h4>

              <p className="font-cormorant text-bone text-2xl mb-3 leading-snug" data-id="element-106">
                Call for Pricing
              </p>
              <p className="text-silver-muted text-sm leading-relaxed mb-5" data-id="element-107">
                Rates are discussed privately during your consultation. Every
                session is tailored to your interests, experience level, and the
                arrangements that best suit you.
              </p>

              <a href="tel:5625480191" className="block text-center px-5 py-3 border border-gold text-gold text-xs uppercase tracking-widest hover:bg-gold hover:text-ink transition-all duration-300 mb-4" data-id="element-108">
                Call 562.548.0191
              </a>

              <p className="text-silver-muted text-xs leading-relaxed mb-4 pt-3 border-t border-gold/15" data-id="element-109">
                Dominance, sensual wellness, and premium add-ons all available.
                Extended sessions and bespoke arrangements upon inquiry.
              </p>
              <p className="text-gold text-xs uppercase tracking-widest" data-id="element-110">
                Black-Owned Business
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="lg:col-span-2" data-id="element-111">
            {status === 'success' ? <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="bg-onyx border border-gold/30 p-12 text-center" data-id="element-112">
                <CheckCircleIcon size={56} className="text-gold mx-auto mb-6" data-id="element-113" />
                <h3 className="font-cormorant text-bone text-3xl mb-4" data-id="element-114">
                  Your Request Has Been Received
                </h3>
                <p className="text-silver-muted leading-relaxed max-w-md mx-auto mb-8" data-id="element-115">
                  Thank you for your inquiry. Madam Yas will review your
                  submission personally and respond within 48 hours.
                </p>

                {/* Tip CTA */}
                <a href="#tip" className="inline-flex items-center gap-2 rounded-full border-2 border-gold/60 text-gold px-7 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-gold hover:text-ink hover:border-gold transition-all duration-300 shadow-[0_0_0_rgba(183,148,212,0)] hover:shadow-[0_0_30px_rgba(183,148,212,0.4)] mb-6" data-id="element-116">
                  Leave a Tip
                  <span aria-hidden className="text-base leading-none" data-id="element-117">
                    ❤
                  </span>
                </a>

                <div data-id="element-118">
                  <button onClick={() => setStatus('idle')} className="text-gold text-xs uppercase tracking-widest hover:text-bone transition-colors" data-id="element-119">
                    Submit Another Request
                  </button>
                </div>
              </motion.div> : <form onSubmit={handleSubmit} className="bg-onyx border border-gold/10 p-8 space-y-6" data-id="element-120">
                <div className="grid md:grid-cols-2 gap-6" data-id="element-121">
                  <div data-id="element-122">
                    <label htmlFor="name" className="block text-silver text-sm uppercase tracking-wider mb-2" data-id="element-123">
                      Full Name *
                    </label>
                    <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full bg-ink border border-gold/20 px-4 py-3 text-bone focus:border-gold focus:outline-none transition-colors" data-id="element-124" />
                  </div>
                  <div data-id="element-125">
                    <label htmlFor="email" className="block text-silver text-sm uppercase tracking-wider mb-2" data-id="element-126">
                      Email Address *
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full bg-ink border border-gold/20 px-4 py-3 text-bone focus:border-gold focus:outline-none transition-colors" data-id="element-127" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6" data-id="element-128">
                  <div data-id="element-129">
                    <label htmlFor="phone" className="block text-silver text-sm uppercase tracking-wider mb-2" data-id="element-130">
                      Phone Number *
                    </label>
                    <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className="w-full bg-ink border border-gold/20 px-4 py-3 text-bone focus:border-gold focus:outline-none transition-colors" data-id="element-131" />
                  </div>
                  <div data-id="element-132">
                    <label htmlFor="experience" className="block text-silver text-sm uppercase tracking-wider mb-2" data-id="element-133">
                      Experience Level *
                    </label>
                    <select id="experience" name="experience" required value={formData.experience} onChange={handleChange} className="w-full bg-ink border border-gold/20 px-4 py-3 text-bone focus:border-gold focus:outline-none transition-colors" data-id="element-134">
                      <option value="" data-id="element-135">Select...</option>
                      <option value="new" data-id="element-136">New to BDSM</option>
                      <option value="some" data-id="element-137">Some Experience</option>
                      <option value="experienced" data-id="element-138">Experienced</option>
                      <option value="advanced" data-id="element-139">Advanced Practitioner</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-140">
                  <label htmlFor="interests" className="block text-silver text-sm uppercase tracking-wider mb-2" data-id="element-141">
                    Areas of Interest *
                  </label>
                  <input type="text" id="interests" name="interests" required value={formData.interests} onChange={handleChange} placeholder="e.g., Discipline, Bondage, Sensory Play" className="w-full bg-ink border border-gold/20 px-4 py-3 text-bone focus:border-gold focus:outline-none transition-colors placeholder:text-silver-muted/50" data-id="element-142" />
                </div>

                <div data-id="element-143">
                  <label htmlFor="message" className="block text-silver text-sm uppercase tracking-wider mb-2" data-id="element-144">
                    Message *
                  </label>
                  <textarea id="message" name="message" required value={formData.message} onChange={handleChange} rows={6} placeholder="Tell me about yourself, your experience, and what you hope to explore..." className="w-full bg-ink border border-gold/20 px-4 py-3 text-bone focus:border-gold focus:outline-none transition-colors resize-none placeholder:text-silver-muted/50" data-id="element-145"></textarea>
                </div>

                {status === 'error' && <div className="flex items-start gap-3 bg-ink border border-red-900/40 px-4 py-3" data-id="element-146">
                    <AlertCircleIcon size={18} className="text-red-400 mt-0.5 flex-shrink-0" data-id="element-147" />
                    <p className="text-red-300 text-sm" data-id="element-148">{errorMessage}</p>
                  </div>}

                <button type="submit" disabled={status === 'submitting'} style={{
              backgroundColor: '#B794D4',
              color: '#07050A'
            }} className="w-full px-8 py-5 text-base uppercase tracking-[0.2em] font-semibold border-2 border-[#B794D4] hover:bg-[#ECE6F5] hover:border-[#ECE6F5] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-[0_0_30px_rgba(183,148,212,0.25)]" data-id="element-149">
                  {status === 'submitting' ? <>
                      <LoaderIcon size={18} className="animate-spin" data-id="element-150" />
                      Submitting…
                    </> : <>
                      Submit Consultation Request
                      <span aria-hidden data-id="element-151">→</span>
                    </>}
                </button>

                <p className="text-silver-muted text-xs text-center leading-relaxed" data-id="element-152">
                  All inquiries are treated with the utmost discretion and
                  confidentiality. You will receive a response within 48 hours.
                </p>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}